import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { verseService } from '../../../services/supabaseService';
import { streakService } from '../../../services/streakService';
import AddVerseModal from '../../verse-library/components/AddVerseModal';

// Helper component for the horizontal progress bar
const PhaseProgressBar = ({ phaseStats }) => {
  // Bucket definitions: which phases belong to which bucket
  const BUCKETS = {
    'Daily': { phases: [1], color: '#ef4444', label: 'Daily Review' },
    'Weekly': { phases: [2, 3], color: '#f97316', label: 'Weekly Review' },
    'Monthly': { phases: [4, 5], color: '#eab308', label: 'Monthly Review' },
    'Quarterly': { phases: [6, 7], color: '#22c55e', label: 'Quarterly Review' },
  };

  // Calculate phase data with proper handling of future verses and phases 1-7
  const phases = [
  {
    id: 0,
    name: 'Future',
    count: phaseStats?.futureVerses || 0,
    color: '#6b7280', // Gray for future verses (was #000000 - unreadable on dark)
    description: 'Verses not yet started',
    bucket: null,
  },
  {
    id: 1,
    name: 'Phase 1',
    count: phaseStats?.phase1Verses || 0,
    color: '#ef4444', // Red - New Learning
    description: 'New Learning (Daily)',
    bucket: 'Daily',
  },
  {
    id: 2,
    name: 'Phase 2',
    count: phaseStats?.phase2Verses || 0,
    color: '#f97316', // Orange - Initial Practice
    description: 'Initial Practice',
    bucket: 'Weekly',
  },
  {
    id: 3,
    name: 'Phase 3',
    count: phaseStats?.phase3Verses || 0,
    color: '#eab308', // Yellow - Building Memory
    description: 'Building Memory',
    bucket: 'Weekly',
  },
  {
    id: 4,
    name: 'Phase 4',
    count: phaseStats?.phase4Verses || 0,
    color: '#84cc16', // Lime - Strengthening
    description: 'Strengthening',
    bucket: 'Monthly',
  },
  {
    id: 5,
    name: 'Phase 5',
    count: phaseStats?.phase5Verses || 0,
    color: '#22c55e', // Green - Consolidation
    description: 'Consolidation',
    bucket: 'Monthly',
  },
  {
    id: 6,
    name: 'Phase 6',
    count: phaseStats?.phase6Verses || 0,
    color: '#06b6d4', // Cyan - Long-term
    description: 'Long-term',
    bucket: 'Quarterly',
  },
  {
    id: 7,
    name: 'Phase 7',
    count: phaseStats?.phase7Verses || 0,
    color: '#3b82f6', // Blue - Mastered
    description: 'Mastered',
    bucket: 'Quarterly',
  }];

  // Calculate bucket totals
  const bucketTotals = Object.entries(BUCKETS)?.reduce((acc, [bucketName, bucketDef]) => {
    acc[bucketName] = bucketDef?.phases?.reduce((sum, phaseId) => {
      const phase = phases?.find(p => p?.id === phaseId);
      return sum + (phase?.count || 0);
    }, 0);
    return acc;
  }, {});

  // Calculate total verses for percentage calculation
  const totalVerses = phases?.reduce((sum, phase) => sum + phase?.count, 0);

  // If no verses, show empty state
  if (totalVerses === 0) {
    return (
      <div className="bg-muted rounded-lg p-4 text-center">
        <p className="text-muted-foreground text-sm">No verses added yet</p>
        <p className="text-xs text-muted-foreground/70 mt-1">Add verses to see your progress</p>
      </div>);

  }

  return (
    <div className="space-y-3">
      <h4 className="font-medium text-foreground mb-2">Memorization Progress</h4>
      {/* Horizontal Progress Bar - 150% thicker with hover tooltips */}
      <div className="relative bg-muted rounded-xl overflow-visible shadow-inner" style={{ height: '3rem' }}>
        <div className="flex h-full rounded-xl overflow-hidden">
        {phases?.map((phase, idx) => {
          const percentage = totalVerses > 0 ? phase?.count / totalVerses * 100 : 0;

          // Only show segments for phases that have verses
          if (phase?.count === 0) return null;

          const bucketInfo = phase?.bucket ? BUCKETS?.[phase?.bucket] : null;
          const bucketTotal = phase?.bucket ? bucketTotals?.[phase?.bucket] : null;

          return (
            <div
              key={phase?.id}
              className="group/seg relative flex items-center justify-center text-white text-xs font-semibold transition-all duration-300 hover:brightness-110 hover:z-10 cursor-default"
              style={{
                width: `${percentage}%`,
                backgroundColor: phase?.color,
                minWidth: percentage > 0 ? '20px' : '0',
                animation: `barFillIn 0.8s cubic-bezier(0.4, 0, 0.2, 1) both`,
                animationDelay: `${idx * 0.08}s`,
              }}>

              {percentage >= 8 &&
              <span className="drop-shadow-sm select-none">
                  {phase?.count}
                </span>
              }

              {/* Enhanced Hover Tooltip with bucket info */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 px-3 py-2.5 bg-popover border border-border rounded-lg shadow-xl text-foreground text-xs whitespace-nowrap opacity-0 invisible group-hover/seg:opacity-100 group-hover/seg:visible transition-all duration-200 z-50 pointer-events-none min-w-[160px]">
                {/* Phase header */}
                <div className="font-semibold mb-1" style={{ color: phase?.color }}>{phase?.name}</div>
                <div className="text-muted-foreground text-[11px] mb-2">{phase?.description}</div>
                
                {/* Phase verse count */}
                <div className="space-y-1 border-t border-border/50 pt-1.5">
                  <div className="flex justify-between gap-4">
                    <span className="text-muted-foreground">This phase:</span>
                    <span className="font-semibold text-foreground">{phase?.count} verse{phase?.count !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-muted-foreground">% of total:</span>
                    <span className="font-semibold text-foreground">{percentage?.toFixed(1)}%</span>
                  </div>
                </div>

                {/* Bucket info */}
                {bucketInfo && bucketTotal !== null && (
                  <div className="mt-2 pt-1.5 border-t border-border/50">
                    <div className="flex items-center gap-1.5 mb-1">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: bucketInfo?.color }} />
                      <span className="font-semibold text-[11px]" style={{ color: bucketInfo?.color }}>{phase?.bucket} Bucket</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-muted-foreground text-[11px]">Bucket total:</span>
                      <span className="font-semibold text-foreground text-[11px]">{bucketTotal} verse{bucketTotal !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      Phases: {BUCKETS?.[phase?.bucket]?.phases?.join(', ')}
                    </div>
                  </div>
                )}

                {/* Tooltip arrow */}
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-border" />
              </div>
            </div>);

        })}
        </div>
      </div>
      {/* Summary Stats */}
      <div className="bg-muted/50 rounded-lg p-3 grid grid-cols-3 gap-4 text-sm">
        <div className="text-center">
          <div className="font-semibold text-foreground">{phaseStats?.totalActiveVerses || 0}</div>
          <div className="text-muted-foreground text-xs">Active</div>
        </div>
        <div className="text-center">
          <div className="font-semibold text-foreground">{totalVerses}</div>
          <div className="text-muted-foreground text-xs">Total</div>
        </div>
        <div className="text-center">
          <div className="font-semibold text-foreground">{phaseStats?.versesDueToday || 0}</div>
          <div className="text-muted-foreground text-xs">Due Today</div>
        </div>
      </div>

      {/* Bucket Summary Row */}
      <div className="grid grid-cols-4 gap-2">
        {Object.entries(BUCKETS)?.map(([bucketName, bucketDef]) => {
          const total = bucketTotals?.[bucketName] || 0;
          return (
            <div
              key={bucketName}
              className="text-center p-2 rounded-lg border border-border/50 bg-muted/30"
              title={`${bucketDef?.label}: ${total} verse${total !== 1 ? 's' : ''} (Phases ${bucketDef?.phases?.join(', ')})`}
            >
              <div className="w-3 h-3 rounded-full mx-auto mb-1" style={{ backgroundColor: bucketDef?.color }} />
              <div className="font-semibold text-foreground text-sm">{total}</div>
              <div className="text-muted-foreground text-[10px] leading-tight">{bucketName}</div>
            </div>
          );
        })}
      </div>
    </div>);

};

export function DailyReviewCard({ onExtraReview }) {
  const navigate = useNavigate();
  const { user, loading: authLoading, userProfile } = useAuth();
  const [dailyReviewCompleted, setDailyReviewCompleted] = useState(false);
  const [dailyReviewStats, setDailyReviewStats] = useState({
    versesToReview: 0,
    estimatedTime: 0,
    dailyBucket: 0,
    weeklyBucket: 0,
    monthlyBucket: 0,
    quarterlyBucket: 0,
    newVerses: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [phaseStats, setPhaseStats] = useState({});
  const [timeUntilMidnight, setTimeUntilMidnight] = useState('');
  const [isAddVerseModalOpen, setIsAddVerseModalOpen] = useState(false);
  const [currentStreak, setCurrentStreak] = useState(0);

  // ── Local-timezone date helper ────────────────────────────────────────────
  // Returns 'YYYY-MM-DD' in the user's LOCAL timezone (not UTC).
  const getLocalDateString = (date = new Date()) => {
    const year = date?.getFullYear();
    const month = String(date?.getMonth() + 1)?.padStart(2, '0');
    const day = String(date?.getDate())?.padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Move loadSpacedRepetitionData function outside useEffect to make it accessible
  const loadSpacedRepetitionData = async () => {
    if (!user?.id || authLoading) return;

    try {
      setLoading(true);
      setError('');

      // Get verses due for review today — use LOCAL date, not UTC
      const { data: reviewVerses, error: reviewError } = await verseService?.getVersesForSpacedRepetitionReview(
        user?.id,
        getLocalDateString() // Local date string YYYY-MM-DD
      );

      if (reviewError) {
        throw new Error(reviewError?.message || 'Failed to load review data');
      }

      // Get comprehensive memorization statistics
      const { data: statsData, error: statsError } = await verseService?.getUserMemorizationStats(user?.id);

      if (statsError) {
        throw new Error(statsError?.message || 'Failed to load statistics');
      }

      const reviewVersesArray = reviewVerses || [];
      const stats = statsData || {
        versesDueToday: 0,
        phase1Count: 0,
        phase2Count: 0,
        phase3Count: 0,
        phase4Count: 0,
        phase5Count: 0,
        phase6Count: 0,
        phase7Count: 0
      };

      // Calculate bucket breakdown based on current phases
      const bucketBreakdown = {
        dailyBucket: stats?.phase1Count || 0, // Phase 1: Daily Reviews
        weeklyBucket: (stats?.phase2Count || 0) + (stats?.phase3Count || 0), // Phase 2-3: Weekly Reviews
        monthlyBucket: (stats?.phase4Count || 0) + (stats?.phase5Count || 0), // Phase 4-5: Monthly Reviews
        quarterlyBucket: (stats?.phase6Count || 0) + (stats?.phase7Count || 0), // Phase 6-7: Quarterly Reviews
        newVerses: stats?.phase1Count || 0 // New verses are in Phase 1
      };

      // Calculate total verses to review by summing verse_count from each reference
      const totalVersesToReview = reviewVersesArray?.reduce((sum, verse) => sum + (verse?.verseCount || verse?.verse_count || 1), 0);
      
      // Calculate estimated time: ~30 seconds (0.5 min) per verse on average for spaced-repetition review
      const estimatedMinutes = Math.max(1, Math.round(totalVersesToReview * 0.5));

      // Update daily review stats with real data
      setDailyReviewStats({
        versesToReview: totalVersesToReview || 0,
        estimatedTime: estimatedMinutes,
        ...bucketBreakdown
      });

    } catch (err) {
      console.error('Daily review data loading error:', err);
      setError(err?.message || 'Failed to load daily review data');
    } finally {
      setLoading(false);
    }
  };

  const loadPhaseStatistics = async () => {
    try {
      setLoading(true);
      const stats = await verseService?.getVerseStatisticsByPhase(user?.id);
      setPhaseStats(stats);
    } catch (error) {
      console.error('Error loading phase statistics:', error);
      setError('Failed to load verse statistics');
    } finally {
      setLoading(false);
    }
  };

  // Load real spaced repetition data from database
  useEffect(() => {
    if (user?.id && !authLoading) {
      loadSpacedRepetitionData();
    }
  }, [user?.id, authLoading]);

  // Check if daily review was completed today
  useEffect(() => {
    const today = getLocalDateString();
    const lastCompletedDate = localStorage.getItem('lastDailyReviewCompleted');
    setDailyReviewCompleted(lastCompletedDate === today);
  }, []);

  // Load phase statistics on component mount
  useEffect(() => {
    if (user?.id && !authLoading) {
      loadPhaseStatistics();
    }
  }, [user?.id, authLoading]);

  // Load streak info
  useEffect(() => {
    if (user?.id && !authLoading) {
      streakService?.getStreakInfo(user?.id)?.then(({ data }) => {
        if (data?.currentStreak != null) setCurrentStreak(data?.currentStreak);
      });
    }
  }, [user?.id, authLoading]);

  // Countdown timer: seconds until midnight in user's timezone
  useEffect(() => {
    const userTimezone = userProfile?.timezone || Intl.DateTimeFormat()?.resolvedOptions()?.timeZone || 'UTC';

    const calcTimeUntilMidnight = () => {
      const now = new Date();

      // Get the current time parts in the user's timezone
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: userTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })?.formatToParts(now);

      const get = (type) => parseInt(parts?.find(p => p?.type === type)?.value || '0', 10);
      const year = get('year');
      const month = get('month');
      const day = get('day');

      // Build midnight in user's timezone as a UTC timestamp
      // "Tomorrow midnight" in user's timezone
      const midnightStr = `${year}-${String(month)?.padStart(2, '0')}-${String(day)?.padStart(2, '0')}T00:00:00`;
      // Parse as if it's in the user's timezone using a trick: find offset
      const midnightInTz = new Date(
        new Date(midnightStr).toLocaleString('en-US', { timeZone: 'UTC' })
      );

      // Use Intl to get the UTC offset for the user's timezone at this moment
      const utcNow = now?.getTime();
      const tzOffset = now?.getTime() - new Date(
        now.toLocaleString('en-US', { timeZone: 'UTC' })
      )?.getTime() + new Date(
        now.toLocaleString('en-US', { timeZone: userTimezone })
      )?.getTime() - now?.getTime();

      // Calculate next midnight in user's tz as UTC ms
      // Current date in user's tz
      const nowInTzMs = now?.getTime() + (
        new Date(now.toLocaleString('en-US', { timeZone: userTimezone }))?.getTime() -
        new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }))?.getTime()
      );
      const nowInTzDate = new Date(nowInTzMs);
      // Midnight of next day in user's tz (as a local-tz Date object)
      const nextMidnightInTz = new Date(nowInTzMs);
      nextMidnightInTz?.setHours(24, 0, 0, 0);

      // Convert back to real UTC
      const tzDiffMs = new Date(now.toLocaleString('en-US', { timeZone: userTimezone }))?.getTime() -
        new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }))?.getTime();
      const nextMidnightUTC = new Date(nextMidnightInTz.getTime() - tzDiffMs);

      const diffMs = nextMidnightUTC?.getTime() - now?.getTime();
      if (diffMs <= 0) return '00:00:00';

      const totalSeconds = Math.floor(diffMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      return `${String(hours)?.padStart(2, '0')}:${String(minutes)?.padStart(2, '0')}:${String(seconds)?.padStart(2, '0')}`;
    };

    setTimeUntilMidnight(calcTimeUntilMidnight());
    const interval = setInterval(() => {
      setTimeUntilMidnight(calcTimeUntilMidnight());
    }, 1000);

    return () => clearInterval(interval);
  }, [userProfile?.timezone]);

  const handleStartDailyReview = () => {
    console.log('Starting daily review...');
    navigate('/daily-review-system');
  };

  // Show loading state during data fetch
  if (authLoading || loading) {
    return (
      <div className="bg-gradient-to-br from-primary/5 via-accent/5 to-success/5 rounded-xl p-4 border border-primary/20 shadow-md">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-xl mb-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
          <h2 className="text-xl font-heading font-bold text-foreground mb-1">
            Loading Daily Review
          </h2>
          <p className="text-sm text-muted-foreground">
            Fetching your verse review schedule...
          </p>
        </div>
      </div>);

  }

  // Show error state if data loading failed
  if (error) {
    return (
      <div className="bg-gradient-to-br from-red/5 via-red/5 to-red/10 rounded-xl p-4 border border-red/20 shadow-md">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-red/10 rounded-xl mb-2">
            <Icon name="AlertCircle" size={24} color="var(--color-red)" />
          </div>
          <h2 className="text-xl font-heading font-bold text-foreground mb-1">
            Unable to Load Review Data
          </h2>
          <p className="text-sm text-muted-foreground mb-3">
            {error}
          </p>
          <Button
            variant="outline"
            onClick={() => window.location?.reload()}
            className="text-sm">

            Try Again
          </Button>
        </div>
      </div>);

  }

  // Show completed state when daily review is done
  if (dailyReviewCompleted) {
    return (
      <div className="bg-gradient-to-br from-success/5 via-success/5 to-success/10 rounded-xl p-4 border border-success/30 shadow-md">
        {/* Condensed header row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center justify-center w-9 h-9 bg-success/20 rounded-lg">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" />
            </div>
            <div>
              <h2 className="text-base font-heading font-bold text-foreground leading-tight">
                Daily Review Complete!
              </h2>
              <p className="text-xs text-muted-foreground">
                {new Date()?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </p>
            </div>
          </div>
          {/* Streak + countdown */}
          <div className="flex flex-col items-end gap-1">
            {currentStreak > 0 && (
              <div className="flex items-center gap-1 bg-orange-500/10 border border-orange-500/20 rounded-lg px-2 py-0.5">
                <Icon name="Flame" size={13} color="#f97316" />
                <span className="text-xs font-bold text-orange-500">{currentStreak} day streak</span>
              </div>
            )}
            {timeUntilMidnight && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <Icon name="Clock" size={11} className="opacity-60" />
                <span className="text-[10px] opacity-70">Resets in</span>
                <span className="font-mono text-xs font-semibold text-foreground/70 tracking-wider">
                  {timeUntilMidnight}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Phase Progress Bar for completed state */}
        <div className="mb-3">
          <PhaseProgressBar phaseStats={phaseStats} />
        </div>

        {/* Extra Practice Option - Compact */}
        <div className="space-y-2">
          <Button
            onClick={() => onExtraReview ? onExtraReview() : navigate('/daily-review-system')}
            className="w-full h-12 text-base font-semibold rounded-full bg-gradient-to-r from-accent to-primary hover:from-accent/90 hover:to-primary/90 border border-accent/20 shadow-md hover:shadow-lg active:scale-[0.99]"
            iconName="RefreshCw"
            iconPosition="left">
            Extra Review
          </Button>
          
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-1">
              Review missed verses and tomorrow's schedule
            </p>
            <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Icon name="RotateCcw" size={10} />
                Missed
              </span>
              <span className="flex items-center gap-1">
                <Icon name="Calendar" size={10} />
                Tomorrow
              </span>
            </div>
          </div>
        </div>
      </div>);

  }

  // Show empty state if no verses to review
  if (dailyReviewStats?.versesToReview === 0) {
    const userTimezone = userProfile?.timezone || Intl.DateTimeFormat()?.resolvedOptions()?.timeZone || 'UTC';

    return (
      <div className="bg-gradient-to-br from-muted/5 via-muted/5 to-muted/10 rounded-xl p-4 border border-muted/20 shadow-md">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-muted/10 rounded-xl mb-2">
            <Icon name="BookOpen" size={24} className="text-muted-foreground" />
          </div>
          <h2 className="text-xl font-heading font-bold text-foreground mb-1">
            No Reviews Today
          </h2>
          <p className="text-sm text-muted-foreground mb-3">
            You're all caught up! Add some verses to start your memorization journey.
          </p>

          {/* Subtle streak countdown timer */}
          {timeUntilMidnight && (
            <div className="flex items-center justify-center gap-2 mb-3 text-muted-foreground">
              <Icon name="Clock" size={13} className="opacity-60" />
              <span className="text-xs opacity-70">Daily reset in</span>
              <span className="font-mono text-sm font-semibold text-foreground/70 tracking-wider">
                {timeUntilMidnight}
              </span>
            </div>
          )}

          {/* Show progress bar even when no reviews due */}
          {(phaseStats?.totalVerses || 0) > 0 &&
          <div className="mb-3">
              <PhaseProgressBar phaseStats={phaseStats} />
            </div>
          }

          {/* Extra Review Button — highlighted with theme accent gradient */}
          <button
            onClick={() => onExtraReview ? onExtraReview() : navigate('/verse-library')}
            className="w-full mb-2 py-3.5 px-4 rounded-full font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-200 bg-gradient-to-r from-accent to-primary text-primary-foreground hover:from-accent/90 hover:to-primary/90 shadow-md hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] border border-accent/20 min-h-[48px]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
            Extra Review
            <span className="text-xs font-normal opacity-80 ml-1">— keep your library fresh</span>
          </button>

          <Button
            variant="outline"
            onClick={() => setIsAddVerseModalOpen(true)}
            iconName="Plus"
            iconPosition="left"
            className="text-sm rounded-full">
            Add Verses
          </Button>
        </div>

        <AddVerseModal
          isOpen={isAddVerseModalOpen}
          onClose={() => setIsAddVerseModalOpen(false)}
          onVerseAdded={() => {
            setIsAddVerseModalOpen(false);
          }}
          userId={user?.id}
        />
      </div>);

  }

  return (
    <div className="bg-card rounded-lg shadow-md p-4 sm:p-6 border border-border">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Daily Review</h3>
        <div className="text-sm text-muted-foreground">
          {new Date()?.toLocaleDateString()}
        </div>
      </div>
      {/* Enhanced review stats with phase breakdown */}
      <div className="space-y-4">
        {/* Today's Review Summary */}
        <div className="bg-primary/10 rounded-lg p-4 border border-primary/20">
          <h4 className="font-medium text-foreground mb-2 text-xl">Today's Reviews</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-primary text-[21px]">Due Today:</span>
              <span className="ml-2 font-bold text-foreground text-3xl">{dailyReviewStats?.versesToReview}</span>
            </div>
            <div>
              <span className="text-red-500 text-[21px]">Overdue:</span>
              <span className="ml-2 font-bold text-foreground text-3xl">{phaseStats?.overdueVerses || 0}</span>
            </div>
          </div>
        </div>

        {/* Phase Progress Bar replaces the table */}
        <PhaseProgressBar phaseStats={phaseStats} />

        {/* Estimated Time */}
        <div className="bg-muted/50 rounded-lg p-4 border border-border">
          <div className="text-sm">
            <span className="text-muted-foreground">Estimated Time:</span>
            <span className="ml-2 font-medium text-foreground">{dailyReviewStats?.estimatedTime} minutes</span>
          </div>
        </div>

        {/* Action Button */}
        <button
          className="w-full bg-primary text-primary-foreground py-3.5 px-4 rounded-full hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all duration-200 font-semibold flex flex-col items-center justify-center gap-0.5 shadow-md hover:shadow-lg active:scale-[0.99] border border-primary/20 min-h-[52px]"
          onClick={handleStartDailyReview}>
          <span className="text-base leading-tight">Start Daily Review ({dailyReviewStats?.versesToReview} verses)</span>
          {timeUntilMidnight && (
            <span className="text-xs font-normal opacity-80">{timeUntilMidnight} left</span>
          )}
        </button>

        {/* Extra Review Button — only shown when NO reviews are due today (one or the other) */}
        {dailyReviewStats?.versesToReview === 0 && (
          <button
            onClick={() => onExtraReview ? onExtraReview() : navigate('/verse-library')}
            className="w-full py-3.5 px-4 rounded-full font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-200 bg-gradient-to-r from-accent to-primary text-primary-foreground hover:from-accent/90 hover:to-primary/90 shadow-md hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] border border-accent/20 min-h-[48px]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
            Extra Review
            <span className="text-xs font-normal opacity-80 ml-1">— review any verse anytime</span>
          </button>
        )}
      </div>
    </div>);

}

export default DailyReviewCard;